/**
 * Все API endpoints BeepApp.
 */
import { api } from "./client"
import type {
  TokenResponse,
  User,
  VehicleCard,
  VehicleSearchResponse,
  VehicleStatus,
  SignalType,
  SignalCounter,
  SignalCategory,
  Garage,
  PersonalGarage,
  CleanStartResult,
  JournalEntry,
  Autoclan,
  AutoclanDetail,
  AutoclanChatState,
  AutoclanInvitePreview,
  AdminDashboard,
  AdminGarageArchivePage,
  AdminGarageRecord,
  ModerationRequest,
  PartnerProfile,
  PromoCodeValidation,
} from "../types/api"

// ─── Auth ────────────────────────────────────────────────────────────────────

export const authApi = {
  loginTelegram: (initData: string, launchId: string) =>
    api.post<TokenResponse>("/auth/telegram", { init_data: initData, launch_id: launchId }),

  getMe: () => api.get<User>("/auth/me"),

  heartbeat: () => api.post<{ ok: boolean; last_seen_at: string }>("/auth/me/heartbeat"),
}

// ─── Vehicles ────────────────────────────────────────────────────────────────

export const vehiclesApi = {
  search: (plate: string) =>
    api.post<VehicleSearchResponse>("/vehicles/search", { plate }),

  getByPlate: (plate: string, countryCode?: string) => {
    const query = countryCode ? `?country_code=${encodeURIComponent(countryCode)}` : ""
    return api.get<VehicleCard>(`/vehicles/by-plate/${encodeURIComponent(plate)}${query}`)
  },

  getCard: (vehicleId: number) =>
    api.get<VehicleCard>(`/vehicles/${vehicleId}/card`, { cache: "no-store" }),

  getMyGarageCard: () =>
    api.get<VehicleCard>("/vehicles/my-garage/card"),

  getSuggestedCard: () =>
    api.get<VehicleCard>("/vehicles/suggested-card"),

  recordView: (vehicleId: number) =>
    api.post<{ ok: boolean }>(`/vehicles/${vehicleId}/view`),

  share: (vehicleId: number) =>
    api.post<{ ok: boolean; deep_link: string; plate_normalized: string }>(
      `/vehicles/${vehicleId}/share`
    ),
}

// ─── Signals ─────────────────────────────────────────────────────────────────

export const signalsApi = {
  getTypes: () => api.get<SignalType[]>("/signal-types"),

  send: (vehicleId: number, signalTypeId: number, allowReply: boolean = false) =>
    api.post<{ id: number }>(`/vehicles/${vehicleId}/signals`, {
      signal_type_id: signalTypeId,
      allow_reply: allowReply,
    }),

  getCounters: (vehicleId: number) =>
    api.get<SignalCounter[]>(`/vehicles/${vehicleId}/signal-counters`),
}

// ─── Garage ───────────────────────────────────────────────────────────────────

export const garageApi = {
  get: (vehicleId: number) =>
    api.get<Garage>(`/garage/${vehicleId}`),

  getMine: () => api.get<PersonalGarage>("/garage/me", { cache: "no-store" }),

  getSignalIndicator: () =>
    api.get<{ category: SignalCategory | null }>("/garage/me/signal-indicator", { cache: "no-store" }),

  checkActivationPlate: (plate: string) =>
    api.get<{ ok: boolean; can_activate: boolean; reason: string | null }>(
      `/garage/activation/plate/check?plate=${encodeURIComponent(plate)}`
    ),

  activate: (plate: string, promoCode?: string, affiliateLink?: string) =>
    api.post<Garage>("/garage/activate", {
      plate,
      payment_stub: true,
      promo_code: promoCode || null,
      affiliate_link: affiliateLink || null,
    }),

  attachVehicle: (garageId: number, plate: string) =>
    api.post<Garage>(`/garage/${garageId}/attach-vehicle`, { plate }),

  checkActivationPromo: (promoCode: string, affiliateLink: string) =>
    api.get<{ ok: boolean; promo_code: string; bonus_years: number }>(
      `/garage/promo/check?promo_code=${encodeURIComponent(promoCode)}&affiliate_link=${encodeURIComponent(affiliateLink)}`
    ),

  createActivationInvoice: (plate: string, promoCode?: string, affiliateLink?: string) =>
    api.post<{ payment_id: number; invoice_url: string; amount_stars: number; purpose: string }>(
      "/stars/activation/invoice",
      {
        plate,
        promo_code: promoCode || null,
        affiliate_link: affiliateLink || null,
      }
    ),

  createRenewInvoice: (garageId: number) =>
    api.post<{ payment_id: number; invoice_url: string; amount_stars: number; purpose: string }>(
      `/stars/garage/${garageId}/renew/invoice`,
      {}
    ),

  createCleanInvoice: (garageId: number) =>
    api.post<{ payment_id: number; invoice_url: string; amount_stars: number; purpose: string }>(
      `/stars/garage/${garageId}/clean/invoice`,
      {}
    ),

  getPaymentStatus: (paymentId: number) =>
    api.get<{ payment_id: number; status: string; purpose: string; amount_stars: number; garage_id: number | null }>(
      `/stars/payments/${paymentId}`,
      { cache: "no-store" }
    ),

  cancelPayment: (paymentId: number) =>
    api.post<{ ok: boolean; status: string }>(`/stars/payments/${paymentId}/cancel`, {}),

  renew: (garageId: number) =>
    api.post<Garage>(`/garage/${garageId}/renew`, { payment_stub: true }),

  deactivate: (garageId: number) =>
    api.post<Garage>(`/garage/${garageId}/deactivate`, {}),

  getJournal: (garageId: number) =>
    api.get<JournalEntry[]>(`/garage/${garageId}/journal`),

  markSignalsRead: (garageId: number, latestSignalId: number) =>
    api.post<{ ok: boolean; changed: boolean; last_signal_read_at: string | null }>(
      `/garage/${garageId}/journal/read-signals`,
      { latest_signal_id: latestSignalId }
    ),

  markUrgentRead: (garageId: number) =>
    api.post<{ ok: boolean; changed: boolean; last_urgent_read_at: string | null }>(
      `/garage/${garageId}/journal/read-urgent`,
      {}
    ),

  resolveJournalReply: (garageId: number, signalId: number, token: string) =>
    api.get<{ username: string }>(
      `/garage/${garageId}/journal/${signalId}/reply?token=${encodeURIComponent(token)}`
    ),

  updatePrivacy: (garageId: number, hideAttentionSignals: boolean) =>
    api.post<Garage>(`/garage/${garageId}/privacy`, {
      hide_attention_signals: hideAttentionSignals,
    }),

  updateStatus: (garageId: number, statusId: number | null) =>
    api.post<{ ok: boolean }>(`/garage/${garageId}/status`, { status_id: statusId }),

  cleanPublicHistory: (garageId: number) =>
    api.post<CleanStartResult>(
      `/garage/${garageId}/clean-public-history`
    ),

  cleanStart: (garageId: number) =>
    api.post<CleanStartResult>(
      `/garage/${garageId}/clean-start`
    ),

  getStatuses: () => api.get<VehicleStatus[]>("/garage/statuses/list"),
}

// ─── Autoclans ────────────────────────────────────────────────────────────────

export const autoclanApi = {
  list: () => api.get<Autoclan[]>("/autoclans"),

  detail: (clanId: number) => api.get<AutoclanDetail>(`/autoclans/${clanId}`),

  create: (name: string, mainVehicleId?: number) =>
    api.post<{ id: number; name: string; created_at: string; member_id: number }>("/autoclans", {
      name,
      main_vehicle_id: mainVehicleId,
    }),

  createInvite: (clanId: number) =>
    api.post<{ invite_code: string; expires_at: string; clan_name: string; chat_url: string | null }>(
      `/autoclans/${clanId}/invite`
    ),

  getChat: (clanId: number) =>
    api.get<AutoclanChatState>(`/autoclans/${clanId}/chat`, { cache: "no-store" }),

  linkChat: (clanId: number, publicUrl: string) =>
    api.post<AutoclanChatState>(`/autoclans/${clanId}/chat`, { public_url: publicUrl }),

  invitePreview: (inviteCode: string) =>
    api.get<AutoclanInvitePreview>(`/autoclans/invite/${inviteCode}`),

  join: (inviteCode: string) =>
    api.post<{ ok: boolean; clan_id: number; clan_name: string }>("/autoclans/join", {
      invite_code: inviteCode,
    }),

  leave: (clanId: number) => api.post<{ ok: boolean }>(`/autoclans/${clanId}/leave`),

  removeMember: (clanId: number, memberId: number) =>
    api.post<{ ok: boolean }>(`/autoclans/${clanId}/members/${memberId}/remove`),

  requestOwnershipTransfer: (clanId: number, memberId: number) =>
    api.post<{ ok: boolean; transfer_id: number; to_user_id: number }>(
      `/autoclans/${clanId}/ownership-transfer`,
      { member_id: memberId }
    ),

  acceptOwnershipTransfer: (clanId: number, transferId: number) =>
    api.post<{ ok: boolean; new_owner_user_id: number }>(
      `/autoclans/${clanId}/ownership-transfer/${transferId}/accept`
    ),

  declineOwnershipTransfer: (clanId: number, transferId: number) =>
    api.post<{ ok: boolean }>(`/autoclans/${clanId}/ownership-transfer/${transferId}/decline`),

  deleteClan: (clanId: number) => api.delete<{ ok: boolean }>(`/autoclans/${clanId}`),
}

// ─── Admin ────────────────────────────────────────────────────────────────────

export const adminApi = {
  getDashboard: (period: "24h" | "7d" | "30d" | "1y" | "all" = "all") =>
    api.get<AdminDashboard>(`/admin/dashboard?period=${period}`),

  getGarageArchive: (cursor?: string, limit = 25) => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (cursor) params.set("cursor", cursor)
    return api.get<AdminGarageArchivePage>(`/admin/garage-archive?${params}`)
  },

  searchGarage: (plate: string) =>
    api.get<AdminGarageRecord>(`/admin/garage-search?plate=${encodeURIComponent(plate)}`),

  unlinkGarage: (target: AdminGarageRecord) =>
    api.post<{ ok: boolean; garage_id: number; vehicle_id: number; garage_remains_active: boolean; expires_at: string | null }>(
      `/garage/${target.garage_id}/unlink-vehicle`,
      {
        vehicle_id: target.vehicle_id,
        plate_normalized: target.plate_normalized,
      }
    ),

  getModerationQueue: () =>
    api.get<ModerationRequest[]>("/admin/moderation/activation-requests"),

  approveActivation: (requestId: number) =>
    api.post<{ ok: boolean; garage_status: string }>(
      `/admin/moderation/activation-requests/${requestId}/approve`
    ),

  sendToRevision: (requestId: number, reason?: string) =>
    api.post<{ ok: boolean }>(
      `/admin/moderation/activation-requests/${requestId}/revision`,
      { reason: reason || null }
    ),

  rejectActivation: (requestId: number, comment?: string) =>
    api.post<{ ok: boolean; attempt_number: number }>(
      `/admin/moderation/activation-requests/${requestId}/reject`,
      { comment }
    ),
}

// ─── Links ────────────────────────────────────────────────────────────────────

export const linksApi = {
  recordClick: (vehicleId?: number) =>
    api.post<{ ok: boolean }>("/links/click", { vehicle_id: vehicleId }),
}


// ─── Partners / Попутчик ─────────────────────────────────────────────────────

export const partnersApi = {
  getMe: () => api.get<PartnerProfile | null>("/partners/me"),

  createPromoCode: (promoCode: string, affiliateLink: string) =>
    api.post<PartnerProfile>("/partners/promo-code", { promo_code: promoCode, affiliate_link: affiliateLink }),

  unbind: () => api.post<PartnerProfile>("/partners/unbind"),
  rebind: (affiliateLink: string) => api.post<PartnerProfile>("/partners/rebind", { affiliate_link: affiliateLink }),
  validate: (promoCode: string, affiliateLink: string) => api.post<{ok: boolean; promo_code: string; affiliate_link: string}>("/partners/validate", { promo_code: promoCode, affiliate_link: affiliateLink }),
  resolveInvite: (promoCode: string) => api.get<{promo_code: string; affiliate_link: string}>(`/partners/invite/${encodeURIComponent(promoCode)}`),

  checkPromoCode: (promoCode: string, garageId: number, affiliateLink: string) =>
    api.get<PromoCodeValidation>(
      `/partners/promo-code/${encodeURIComponent(promoCode)}/check?garage_id=${garageId}&affiliate_link=${encodeURIComponent(affiliateLink)}`
    ),
}
